/**
 * Optimize Page
 *
 * Shows optimization progress and diff view for results.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  ProgressBar,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Tabs,
  Box,
  Divider,
  SkeletonBodyText,
  Badge,
  Modal,
  List,
} from '@shopify/polaris';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useApi } from '../providers/ApiProvider';
import type { Product, OptimizationJob } from '../types';

export function OptimizePage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const api = useApi();

  const [job, setJob] = useState<OptimizationJob | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const pollInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  // Get productIds from navigation state or URL
  const productIds = (location.state as { productIds?: string[] })?.productIds || [];
  const singleProductId = searchParams.get('product');

  // Load single product if specified
  const loadProduct = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const data = await api.getProduct(id);
      setProduct(data);
    } catch (err) {
      setError('Fehler beim Laden des Produkts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (singleProductId) {
      loadProduct(singleProductId);
    }
  }, [singleProductId, loadProduct]);

  // Poll job status
  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const data = await api.getOptimizationJob(jobId);
      setJob(data);

      if (data.status === 'completed' || data.status === 'failed') {
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
          pollInterval.current = null;
        }
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  }, [api]);

  // Start optimization
  const handleStartOptimization = async () => {
    const ids = singleProductId ? [singleProductId] : productIds;
    if (ids.length === 0) {
      setError('Keine Produkte ausgewählt');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const newJob = await api.startOptimization(ids);
      setJob(newJob);

      // Start polling
      pollInterval.current = setInterval(() => {
        pollJobStatus(newJob.id);
      }, 2000);
    } catch (err) {
      setError('Fehler beim Starten der Optimierung');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Apply optimizations
  const handleApply = async () => {
    const ids = singleProductId ? [singleProductId] : productIds;

    try {
      setApplying(true);
      await api.applyOptimizations(ids);
      setApplyModalOpen(false);
      navigate('/products');
    } catch (err) {
      setError('Fehler beim Anwenden der Optimierungen');
      console.error(err);
    } finally {
      setApplying(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  const tabs = [
    { id: 'description', content: 'Beschreibung' },
    { id: 'usecases', content: 'Use Cases' },
    { id: 'faq', content: 'FAQ' },
    { id: 'specs', content: 'Spezifikationen' },
  ];

  // Render diff view for single product
  const renderDiffView = () => {
    if (!product) return null;

    return (
      <Card>
        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
          <Box padding="400">
            {selectedTab === 0 && (
              <BlockStack gap="400">
                <Text as="h3" variant="headingSm">Original</Text>
                <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                  <div dangerouslySetInnerHTML={{ __html: product.descriptionOriginal || '' }} />
                </Box>
                <Divider />
                <Text as="h3" variant="headingSm">Optimiert</Text>
                <Box background="bg-surface-success" padding="300" borderRadius="200">
                  <div dangerouslySetInnerHTML={{ __html: product.descriptionOptimized || '' }} />
                </Box>
              </BlockStack>
            )}

            {selectedTab === 1 && (
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Use Cases</Text>
                <List type="bullet">
                  {product.usecases?.map((usecase, i) => (
                    <List.Item key={i}>{usecase}</List.Item>
                  ))}
                </List>
              </BlockStack>
            )}

            {selectedTab === 2 && (
              <BlockStack gap="400">
                <Text as="h3" variant="headingSm">Häufig gestellte Fragen</Text>
                {product.faq?.map((item, i) => (
                  <Card key={i}>
                    <BlockStack gap="200">
                      <Text as="p" fontWeight="bold">{item.question}</Text>
                      <Text as="p">{item.answer}</Text>
                    </BlockStack>
                  </Card>
                ))}
              </BlockStack>
            )}

            {selectedTab === 3 && (
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Technische Spezifikationen</Text>
                {product.specs?.map((spec, i) => (
                  <InlineStack key={i} gap="200">
                    <Text as="span" fontWeight="bold">{spec.key}:</Text>
                    <Text as="span">{spec.value}</Text>
                  </InlineStack>
                ))}
              </BlockStack>
            )}
          </Box>
        </Tabs>
      </Card>
    );
  };

  // Render job progress
  const renderJobProgress = () => {
    if (!job) return null;

    const progress = job.totalProducts > 0
      ? Math.round((job.processedProducts / job.totalProducts) * 100)
      : 0;

    return (
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between">
            <Text as="h3" variant="headingMd">Optimierung läuft</Text>
            <Badge tone={job.status === 'completed' ? 'success' : job.status === 'failed' ? 'critical' : 'info'}>
              {job.status === 'pending' ? 'Wartend' :
               job.status === 'processing' ? 'Verarbeite' :
               job.status === 'completed' ? 'Abgeschlossen' :
               'Fehlgeschlagen'}
            </Badge>
          </InlineStack>

          <ProgressBar progress={progress} />

          <InlineStack gap="400">
            <Text as="span" variant="bodySm">
              {job.processedProducts} / {job.totalProducts} Produkte
            </Text>
            <Text as="span" variant="bodySm" tone="success">
              {job.successCount} erfolgreich
            </Text>
            {job.errorCount > 0 && (
              <Text as="span" variant="bodySm" tone="critical">
                {job.errorCount} Fehler
              </Text>
            )}
          </InlineStack>

          {job.status === 'completed' && (
            <Button variant="primary" onClick={() => setApplyModalOpen(true)}>
              Optimierungen anwenden
            </Button>
          )}
        </BlockStack>
      </Card>
    );
  };

  if (loading && !job && !product) {
    return (
      <Page title="Optimieren">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const selectedCount = singleProductId ? 1 : productIds.length;
  const showStartButton = !job && selectedCount > 0;
  const showDiffView = product && product.status === 'optimized';

  return (
    <Page
      title="Optimieren"
      backAction={{ content: 'Zurück', onAction: () => navigate(-1) }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {showStartButton && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  {selectedCount} {selectedCount === 1 ? 'Produkt' : 'Produkte'} ausgewählt
                </Text>
                <Text as="p" tone="subdued">
                  Die KI analysiert deine Produktbeschreibungen und optimiert sie für bessere Sichtbarkeit in KI-Suchmaschinen wie ChatGPT und Perplexity.
                </Text>
                <Button variant="primary" onClick={handleStartOptimization} loading={loading}>
                  Optimierung starten
                </Button>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {job && (
          <Layout.Section>
            {renderJobProgress()}
          </Layout.Section>
        )}

        {showDiffView && (
          <Layout.Section>
            {renderDiffView()}
          </Layout.Section>
        )}

        {selectedCount === 0 && !job && (
          <Layout.Section>
            <Banner>
              Wähle Produkte auf der Produkte-Seite aus, um sie zu optimieren.
            </Banner>
          </Layout.Section>
        )}
      </Layout>

      <Modal
        open={applyModalOpen}
        onClose={() => setApplyModalOpen(false)}
        title="Optimierungen anwenden?"
        primaryAction={{
          content: 'Anwenden',
          onAction: handleApply,
          loading: applying,
        }}
        secondaryActions={[
          {
            content: 'Abbrechen',
            onAction: () => setApplyModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            Die optimierten Beschreibungen werden direkt in Shopify übernommen.
            Die ursprünglichen Beschreibungen bleiben als Backup gespeichert.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
